import React, {useCallback, useEffect, useState} from 'react';
import {Button, Checkbox, Input, message, Progress, Row, Space, Spin, Table, Tabs, Tag} from "antd";
import {ColumnsType} from "antd/es/table";
import AmazonS3Service from "../services/AmazonS3Service";
import filesize from "filesize";
import {useFetch} from "../hooks/useFetch";
import {ImmortalAutoSizer} from "./Misc/ImmortalAutoSizer";
import ProjectsService from "../services/ProjectsService";
// @ts-ignore
import {v4 as uuidv4} from 'uuid';
import {useBoundPollingQueue} from "../contexts/PollingQueue";
import {JobProgress} from "../services/ApiModels/JobProgress";
import {BeforeUnloadPrompt} from "./Misc/BeforeUnloadPrompt";
// @ts-ignore
import {Prompt} from "react-router-dom";

export function ImportRunFromS3Bulk({projectId}: {
    projectId: string,
}) {
    const [bulkImportProcessing, processingTip, runImport, runs] = useBulkImportProcessing(projectId);
    return (
        <>
            <UnloadPrompt when={bulkImportProcessing}/>
            <div style={{width: '100%', height: '100%'}}>
                <Tabs className="fill-height-tabs" defaultActiveKey={"bucket-content"} tabPosition={"bottom"}>
                    <Tabs.TabPane tab={"Bucket Content"} key={"bucket-content"}>
                        <ImmortalAutoSizer>
                            {({width, height}) => (
                                <div style={{width: width + "px", height: height + "px"}}>
                                    <Spin spinning={bulkImportProcessing} tip={processingTip}>
                                        <S3BucketRuns
                                            width={width}
                                            height={height}
                                            runImport={runImport}
                                        />
                                    </Spin>
                                </div>
                            )}
                        </ImmortalAutoSizer>
                    </Tabs.TabPane>
                    {runs.map(x => (
                        <Tabs.TabPane key={x.key} tab={x.name}>
                            <ImmortalAutoSizer>
                                {({width, height}) => (
                                    <div style={{width: width + "px", height: height + "px"}}>
                                        <AttemptTable
                                            width={width}
                                            height={height}
                                            attempt={x}
                                        />
                                    </div>
                                )}
                            </ImmortalAutoSizer>
                        </Tabs.TabPane>
                    ))}
                </Tabs>
            </div>
        </>
    );
}

interface DataType {
    s3_key: string,
    size: number,
    type: "file",
    upload_date: string,
    auto_expose: boolean,
    run_name: string,
}

function S3BucketRuns({height, width, runImport}: {
    height: number,
    width: number,
    runImport: (data: DataType[]) => Promise<BulkImportAttempt>
}) {
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [data, setData] = useState<DataType[]>([]);
    const [lastRunStatus, setLastRunStatus] = useState<string | undefined>(undefined);

    useFetch(
        () => AmazonS3Service.getBucketContentRecursive({bucket_alias: "IMPORT_S3"}, {aws_profile: "IMPORT_S3"}),
        data => setData(data.map(x => ({
            ...x,
            auto_expose: false,
            run_name: x.s3_key
        }))),
        () => message.error("Failed to fetch bucket"),
        []
    );

    function toggleExposeAll() {
        setData(prev => prev.map(x => ({...x, auto_expose: !x.auto_expose})));
    }

    const columns: ColumnsType<DataType> = [
        {
            title: 'Name',
            dataIndex: 's3_key',
            filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => (
                <div style={{padding: 8}}>
                    <Input
                        placeholder="Search name"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{marginBottom: 8, display: 'block'}}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            size="small"
                            style={{width: 90}}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                clearFilters?.();
                                confirm();
                            }}
                            size="small"
                            style={{width: 90}}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) => {
                return record.s3_key.toLowerCase().includes((value as string).toLowerCase());
            },
        },
        {
            title: 'Modified date',
            dataIndex: 'upload_date',
        },
        {
            title: 'Size',
            dataIndex: 'size',
            sorter: true,
            render: (text) => text != null ? filesize(text) : null,
        },
        {
            title: 'Run Name',
            dataIndex: 'run_name',
            render: (value, record) => (
                <RunNameInput
                    value={value}
                    onChange={(value) => {
                        record.run_name = value;
                    }}
                />
            ),
        },
        {
            title: (
                <Row justify={"space-between"}>
                    <Space align={"center"}><span>Auto Expose</span></Space>
                    <Button type={"link"} onClick={toggleExposeAll}>Expose All</Button>
                </Row>
            ),
            dataIndex: 'auto_expose',
            render: (value, record) => (
                <AutoExposeCheckbox
                    value={value}
                    onChange={(value) => {
                        record.auto_expose = value;
                    }}
                />
            ),
        },
    ];

    const footer = () => (
        <>
            <Row justify={"end"}>
                <Space>
                    <span>Rows selected: {selectedRowKeys.length}</span>
                    <Button
                        size={"small"}
                        type={"primary"}
                        onClick={async () => {
                            setLastRunStatus("PROCESSING...");
                            const attempt = await runImport(data.filter(x => selectedRowKeys.includes(x.s3_key)));
                            setLastRunStatus(attempt.status);
                            message.success("Success");
                        }}
                    >
                        Run Import
                    </Button>
                    {lastRunStatus && (
                        <span>{lastRunStatus}</span>
                    )}
                </Space>
            </Row>
        </>
    );

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
            setSelectedRowKeys(selectedRowKeys);
        },
    };

    return (
        <Table
            size={"small"}
            rowSelection={{
                type: "checkbox",
                ...rowSelection,
            }}
            rowKey={record => record.s3_key}
            columns={columns}
            dataSource={data}
            pagination={false}
            footer={footer}
            scroll={{y: height - 100, x: width}}
        />
    );
}

function AttemptTable({attempt, width, height}: {
    attempt: BulkImportAttempt,
    height: number,
    width: number,
}) {
    const columns: ColumnsType<BulkImportAttemptItem> = [
        {
            title: 'Name',
            dataIndex: ['origin', 's3_key'],
        },
        {
            title: 'Schedule Status',
            dataIndex: ['status', 'type'],
        },
        {
            title: 'Import Status',
            render: (_, record) => (
                <FetchStatus projectId={attempt.projectId} item={record}/>
            )
        },
        {
            title: 'Import Task Id',
            render: (_, record) => {
                if (record.status.type === "SCHEDULED") {
                    return record.status.taskCid;
                } else {
                    return undefined;
                }
            }
        }
    ];

    return (
        <Table
            size={"small"}
            rowKey={record => record.origin.s3_key}
            columns={columns}
            dataSource={attempt.items}
            pagination={{
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100", "1000"],
            }}
            scroll={{y: height - 100, x: width}}
        />
    );
}

function UnloadPrompt({when}: {when: boolean}) {
    return (
        <>
            <BeforeUnloadPrompt when={when}/>
            <Prompt message={() => {
                return when ? "Are you sure to leave the page? The import progress will be lost." : true;
            }}/>
        </>
    );
}

function FetchStatus({projectId, item}: { item: BulkImportAttemptItem, projectId: string }) {
    const pollingQueue = useBoundPollingQueue();
    const [importProgress, setImportProgress] = useState<JobProgress | undefined>(undefined);

    useEffect(() => {
        if (item.status.type === "SCHEDULED") {
            const cid = item.status.taskCid;
            pollingQueue.enqueue({
                fetchFactory: async () => {
                    try {
                        const progress = await ProjectsService.importRunFromS3Progress(projectId, cid);
                        setImportProgress(progress.copy());
                        return progress.isCompleted();
                    } catch (err) {
                        console.error(err);
                        return true;
                    }
                },
                pollingRateMillis: 2500
            })
        }
    }, []);

    if (importProgress == null) return (
        <Tag color={"blue"}>fetching...</Tag>
    );

    if (importProgress.isFinished()) return (
        <Tag color={"green"}>completed</Tag>
    )

    if (importProgress.isFailed()) return (
        <Tag color={"red"}>error</Tag>
    )

    if (importProgress.isProgress()) return (
        <Space>
            <span>{importProgress.toProgressInfo()!.status}</span>
            <Progress percent={importProgress.toProgressInfo()!.percent}/>
        </Space>
    )

    return (
        <Tag>unknown...</Tag>
    )
}

type BulkImportAttemptItem = {
    origin: DataType,
    status: { type: "SCHEDULED", taskCid: string } | { type: "FAILED", reason: string } | { type: "PENDING" }
}

type BulkImportAttempt = {
    status: string;
    projectId: string,
    name: string,
    key: string,
    items: BulkImportAttemptItem[],
}

function loadAttempts(projectId: string) {
    try {
        const stored = localStorage.getItem(`bulkImportAttempts:${projectId}`);
        console.log(stored);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.warn("Failed to parse bulk import attempts from localStorage", e);
        return [];
    }
}

function saveAttempts(projectId: string, attempts: BulkImportAttempt[]) {
    try {
        localStorage.setItem(`bulkImportAttempts:${projectId}`, JSON.stringify(attempts));
    } catch (e) {
        console.warn("Failed to store bulk import attempts", e);
    }
}

function useBulkImportProcessing(projectId: string) {
    const [processingTip, setProcessingTip] = useState<string>("");
    const [bulkImportProcessing, setBulkImportProcessing] = useState<boolean>(false);
    const [attempts, setAttempts] = useState<BulkImportAttempt[]>(() => {
        return loadAttempts(projectId);
    });

    const runImport = useCallback(async (selectedData: DataType[]): Promise<BulkImportAttempt> => {
        if (bulkImportProcessing) throw new Error("BulkImport processing started");
        const now = new Date();
        const formattedTime = now.toISOString().replace(/[:.]/g, '-');
        const attempt: BulkImportAttempt = {
            status: "PENDING",
            projectId,
            name: `Import_${formattedTime}`,
            key: uuidv4(),
            items: [],
        };
        setBulkImportProcessing(true);
        try {
            for (let i = 0; i < selectedData.length; i++) {
                setProcessingTip(`Processing... ${i + 1} / ${selectedData.length}. Do not close the page`);
                const params = selectedData[i];
                const attemptItem: BulkImportAttemptItem = {
                    origin: params,
                    status: {type: "PENDING"},
                };
                attempt.items.push(attemptItem);
                try {
                    const taskCid = await ProjectsService.importRunFromS3(projectId, {
                        "run-name": params.run_name,
                        "auto-expose": params.auto_expose,
                        "object-key": params.s3_key
                    });
                    attemptItem.status = {
                        type: "SCHEDULED",
                        taskCid,
                    }
                } catch (error: any) {
                    attemptItem.status = {
                        type: "FAILED",
                        reason: error.message,
                    }
                }
            }
            attempt.status = "FINISHED";
        } catch (error: any) {
            attempt.status = "FAILED";
            console.error(error);
            message.error(`Something went wrong: ${error.message}.`);
        } finally {
            setBulkImportProcessing(false);
            setAttempts(prev => {
                const next = [attempt, ...prev];
                next.length = Math.min(next.length, 5);
                saveAttempts(projectId, next);
                return next;
            });
        }
        return attempt;
    }, []);

    return [bulkImportProcessing, processingTip, runImport, attempts] as const;
}

function AutoExposeCheckbox({value, onChange}: { value: boolean, onChange: (value: boolean) => void }) {
    const [currentValue, setCurrentValue] = useState(value);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    return (
        <Checkbox
            checked={currentValue}
            onChange={(e) => {
                onChange(e.target.checked);
                setCurrentValue(e.target.checked);
            }}
        />
    );
}

function RunNameInput({value, onChange}: { value: string, onChange: (value: string) => void }) {
    const [currentValue, setCurrentValue] = useState(value);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    return (
        <Input
            value={currentValue}
            onChange={(e) => {
                onChange(e.target.value);
                setCurrentValue(e.target.value);
            }}
        />
    );
}